import React from 'react';
import { Box, Typography, Button, Paper, TextField } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { REACT_APP_API_URL } from '../config/env';
import { consumeSessionExpired } from '../services/sessionExpiry';

const API_BASE_URL = REACT_APP_API_URL;

interface LoginPageProps {
  pending?: boolean;
  userName?: string;
}

export default function LoginPage({ pending, userName }: LoginPageProps) {
  // Why the user is looking at this page. Read once on mount and cleared, so
  // an ordinary sign-in later is never mislabelled as an expiry. Without it a
  // session that timed out mid-work dumped people here with no explanation —
  // indistinguishable from having been signed out for no reason.
  const [expired] = React.useState(() => consumeSessionExpired());

  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE_URL}/auth/google`;
  };

  // Super Admin email + password sign-in (2026-10-12) — for signing in on a
  // computer where Google is not an option. The link only shows when the server
  // has it switched on (two Railway env vars); the cookie it gets ends when the
  // browser closes.
  const [passwordLoginEnabled, setPasswordLoginEnabled] = React.useState(false);
  const [showPasswordForm, setShowPasswordForm] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [signingIn, setSigningIn] = React.useState(false);
  const [passwordError, setPasswordError] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetch(`${API_BASE_URL}/auth/password-login`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => setPasswordLoginEnabled(!!body?.data?.enabled))
      .catch(() => setPasswordLoginEnabled(false));
  }, []);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setSigningIn(true);
    setPasswordError(null);
    try {
      const r = await fetch(`${API_BASE_URL}/auth/password-login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (r.ok) {
        window.location.assign('/');
        return;
      }
      setPasswordError(r.status === 429
        ? 'Too many attempts. Wait 15 minutes and try again.'
        : 'That email and password did not match.');
    } catch {
      setPasswordError('Could not reach the server. Try again.');
    }
    setSigningIn(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(201,169,97,0.04) 0%, transparent 70%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 5,
          maxWidth: 420,
          width: '100%',
          textAlign: 'center',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'rgba(201,169,97,0.15)',
          borderRadius: 3,
        }}
      >
        {/* Logo / Brand */}
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{
            mb: 0.5,
            background: 'linear-gradient(135deg, #f76100 0%, #ffb58a 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.02em',
          }}
        >
          BSG
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4, fontSize: '13.5px' }}>
          Company Operating System
        </Typography>

        {pending ? (
          /* Pending approval state */
          <Box>
            <Box
              sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2.5,
                borderRadius: '50%',
                bgcolor: 'rgba(201,169,97,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
              }}
            >
              {'\u23F3'}
            </Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary', mb: 1 }}>
              Welcome, {userName || 'there'}!
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
              Your account is waiting for an admin to approve it and assign a role. You&rsquo;ll be able to sign in once that&rsquo;s done.
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              The admins have been notified. Check back shortly, or ask one of them directly.
            </Typography>
          </Box>
        ) : (
          /* Login state */
          <Box>
            {expired && (
              <Box
                role="status"
                sx={{
                  mb: 3,
                  px: 2,
                  py: 1.5,
                  textAlign: 'left',
                  borderLeft: '2px solid rgba(247,97,0,0.9)',
                  bgcolor: 'rgba(247,97,0,0.08)',
                  borderRadius: '4px',
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.25 }}>
                  Your session expired
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                  You were signed out after a long stretch. Sign in again to pick up where you left off.
                </Typography>
              </Box>
            )}

            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3.5 }}>
              Sign in to access the dashboard
            </Typography>

            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
              sx={{
                py: 1.5,
                bgcolor: 'var(--paper)',
                color: 'var(--ink-soft)',
                fontWeight: 600,
                fontSize: '14px',
                textTransform: 'none',
                borderRadius: '6px',
                '&:hover': {
                  bgcolor: 'var(--paper-2)',
                },
              }}
            >
              Sign in with Google
            </Button>

            <Typography variant="caption" sx={{ display: 'block', mt: 3, color: 'text.disabled' }}>
              Use your company Google account to sign in
            </Typography>

            {passwordLoginEnabled && !showPasswordForm && (
              <Button
                variant="text"
                size="small"
                onClick={() => setShowPasswordForm(true)}
                sx={{ mt: 2, textTransform: 'none', color: 'text.secondary' }}
              >
                Admin sign-in
              </Button>
            )}

            {passwordLoginEnabled && showPasswordForm && (
              <Box component="form" onSubmit={handlePasswordLogin} sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5, textAlign: 'left' }}>
                <TextField
                  label="Email"
                  type="email"
                  size="small"
                  autoComplete="off"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Password"
                  type="password"
                  size="small"
                  autoComplete="off"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                />
                {passwordError && (
                  <Typography variant="body2" color="error" role="alert">{passwordError}</Typography>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={signingIn || !email || !password}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {signingIn ? 'Signing in…' : 'Sign in'}
                </Button>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  On a computer that is not yours, close the browser when you are done.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
